import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, updateDoc, arrayUnion } from "firebase/firestore";
import { ref, set } from "firebase/database";
import { firestore, database } from "../../../firebaseConfig";
import toast from "react-hot-toast";
import { Building2, Search, X } from "lucide-react";

/**
 * AddCompanyModal
 * 
 * Allows clients to send connection requests to companies.
 * 
 * Flow:
 * 1. Load available companies from Firestore
 * 2. Client selects a company
 * 3. Create request in RTDB at Companies/{companyName}/ClientRequests/{uid}
 * 4. Add to user's pendingCompanyRequests array in Firestore
 */

const AddCompanyModal = ({ open, onClose, user, profile, connectedCompanies, onRequestSent }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sending, setSending] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [clientDescription, setClientDescription] = useState("");

  useEffect(() => {
    if (!open) {
      // Reset description when modal closes
      setClientDescription("");
      setSelectedCompany(null);
      return;
    }

    const loadCompanies = async () => {
      setLoading(true);
      try {
        const col = collection(firestore, "companies");
        const snapshot = await getDocs(col);
        const companyList = [];
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          // Filter out companies the client is already connected to
          if (!connectedCompanies.includes(data.companyName)) {
            companyList.push({
              id: doc.id,
              ...data,
            });
          }
        });

        setCompanies(companyList);
      } catch (error) {
        console.error("Error loading companies:", error);
        toast.error("Failed to load companies");
      } finally {
        setLoading(false);
      }
    };

    loadCompanies();
  }, [open, connectedCompanies]);

  const handleSendRequest = async (company) => {
    if (!user || !profile) {
      toast.error("User profile not loaded");
      return;
    }

    // Check if already has a pending request for this company
    const hasPendingRequest = profile.pendingCompanyRequests?.some(
      (req) => req.companyName === company.companyName
    );

    if (hasPendingRequest) {
      toast.error("You already have a pending request to this company");
      return;
    }

    // Require description like farmer flow
    if (!clientDescription.trim()) {
      toast.error("Please provide a brief description about yourself");
      return;
    }

    setSending(true);
    try {
      const uid = user.uid;
      const now = new Date().toISOString();

      // Create request in RTDB for company to see
      const requestRef = ref(
        database,
        `Companies/${company.companyName}/ClientRequests/${uid}`
      );
      await set(requestRef, {
        uid,
        clientName: `${profile.firstName || ""} ${profile.lastName || ""}`.trim() || profile.email,
        email: profile.email || "",
        phoneNumber: profile.phoneNumber || "",
        location: profile.location || "",
        description: clientDescription.trim(), // Add description like farmer flow
        requestedAt: now,
        status: "pending",
      });

      // Also store in ClientCompanyRequests for client's reference
      const clientRequestRef = ref(
        database,
        `ClientCompanyRequests/${uid}/${company.companyName}`
      );
      await set(clientRequestRef, {
        companyName: company.companyName,
        requestedAt: now,
        status: "pending",
      });

      // Add to user's pendingCompanyRequests in Firestore
      await updateDoc(doc(firestore, "users", uid), {
        pendingCompanyRequests: arrayUnion({
          companyName: company.companyName,
          requestedAt: now,
          status: "pending",
        }),
      });

      toast.success(`Connection request sent to ${company.companyName}`);
      onRequestSent();
    } catch (error) {
      console.error("Error sending request:", error);
      toast.error("Failed to send request");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  const filteredCompanies = companies.filter((company) =>
    company.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.registrationNumber?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl p-6 shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold text-yellow-700 flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            Connect to Company
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search companies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
          </div>
        </div>

        {/* Companies List */}
        {loading ? (
          <div className="text-center py-8 text-gray-500">
            Loading companies...
          </div>
        ) : filteredCompanies.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {searchTerm ? "No companies found matching your search" : "No companies available to connect"}
          </div>
        ) : (
          <>
            <div className="space-y-3 mb-4 max-h-[40vh] overflow-y-auto">
              {filteredCompanies.map((company) => (
                <CompanyListItem
                  key={company.id}
                  company={company}
                  selected={selectedCompany?.id === company.id}
                  onSelect={() => setSelectedCompany(company)}
                />
              ))}
            </div>

            {/* Description Field */}
            {selectedCompany && (
              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  About You <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={clientDescription}
                  onChange={(e) => setClientDescription(e.target.value)}
                  placeholder="Briefly describe who you are and where you come from. This helps the company identify and attach you correctly."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-none"
                  maxLength={300}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {clientDescription.length}/300 characters
                </p>
              </div>
            )}

            {/* Send Button */}
            {selectedCompany && (
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setSelectedCompany(null);
                    setClientDescription("");
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                >
                  Cancel Selection
                </button>
                <button
                  onClick={() => handleSendRequest(selectedCompany)}
                  disabled={sending || !clientDescription.trim()}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    sending || !clientDescription.trim()
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-yellow-600 text-white hover:bg-yellow-700"
                  }`}
                >
                  {sending ? "Sending..." : "Send Request"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Helper Component
const CompanyListItem = ({ company, selected, onSelect }) => (
  <div 
    onClick={onSelect}
    className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition ${
      selected 
        ? "border-yellow-500 bg-yellow-50" 
        : "border-gray-200 hover:border-yellow-500"
    }`}
  >
    <div className="flex items-center gap-3">
      {company.logoUrl ? (
        <img
          src={company.logoUrl}
          alt={company.companyName}
          className="w-12 h-12 rounded-full object-cover"
        />
      ) : (
        <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-700 font-bold">
          {company.companyName?.charAt(0).toUpperCase()}
        </div>
      )}
      <div>
        <p className="font-semibold text-gray-800">{company.companyName}</p>
        {company.registrationNumber && (
          <p className="text-xs text-gray-500">
            Reg: {company.registrationNumber}
          </p>
        )}
        {company.location && (
          <p className="text-xs text-gray-500">{company.location}</p>
        )}
      </div>
    </div>
    {selected && (
      <div className="text-yellow-600 font-medium">
        Selected ✓
      </div>
    )}
  </div>
);

export default AddCompanyModal;
